import clsx from "clsx";
import { ChevronRight, CircleCheck, Search, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";
import type { HomeAlert, HomeCard, HomeProgress, HomeResponse } from "../../shared/home";
import type { ToolMeta } from "../../shared/tools";
import { comingSoonTools, webTools } from "../../tools/web";
import { Icon } from "../components/Icon";
import { useWithWindows } from "../components/ToolWindows";
import { SearchGroups, searchPlaceholder, useSearchResults } from "../components/SearchAnywhere";
import { Badge, ErrorNotice, PageHeader, Panel, ProgressBar, Spinner } from "../components/ui";
import { useCompany } from "../lib/company";
import { useHome } from "../lib/home";
import { useMe } from "../lib/queries";

/*
 * Home: search across every tool, what needs attention, how far setting up has got, and the tools
 * with a few figures each.
 * Each tool puts its own alerts, figures and results here (its home.ts, via /api/c/:id/home), and
 * they update live with the data they come from.
 */

export function Home() {
    const company = useCompany();
    const { data: me } = useMe();
    const tools = company.tools.map((id) => webTools[id]?.meta).filter((m): m is ToolMeta => m !== undefined);
    const firstName = me?.user.name?.split(" ")[0];
    const [query, setQuery] = useState("");
    const home = useHome();

    return (
        <div className="page">
            <PageHeader title={firstName ? `Hi ${firstName}` : "Home"} subtitle={company.name} />
            {tools.length ? (
                <>
                    <SearchBox query={query} onChange={setQuery} tools={tools} />
                    {query.trim() ? (
                        <SearchResults query={query} tools={tools} />
                    ) : (
                        <div className="grid gap-x-6 lg:grid-cols-3">
                            <div className="lg:col-span-2">
                                <Attention home={home.data} loading={home.isLoading} error={home.error} tools={tools} />
                                <Setup home={home.data} tools={tools} />
                            </div>
                            <ToolCards home={home.data} tools={tools} />
                        </div>
                    )}
                </>
            ) : (
                <Panel>
                    <div className="p-2 text-center text-sm text-muted">No tools yet. They&apos;ll appear here as they&apos;re added.</div>
                </Panel>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------------------------

function SearchBox(props: Readonly<{ query: string; onChange: (q: string) => void; tools: readonly ToolMeta[] }>) {
    return (
        <label className="relative mb-6 block">
            <span className="sr-only">Search</span>
            <Search className="pointer-events-none absolute top-1/2 left-3.5 size-5 -translate-y-1/2 text-muted" />
            <input
                type="search"
                className="input py-3 pl-11 text-base"
                placeholder={searchPlaceholder(props.tools)}
                value={props.query}
                onChange={(e) => props.onChange(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "Escape") props.onChange("");
                    // Enter opens the top result (SearchResults marks it).
                    if (e.key === "Enter") {
                        const first = document.querySelector<HTMLAnchorElement>("[data-first-hit]");
                        // Clicked, as the link it is: its href already has the business in it (navigating to it would add it again).
                        first?.click();
                    }
                }}
            />
        </label>
    );
}

function SearchResults(props: Readonly<{ query: string; tools: readonly ToolMeta[] }>) {
    const { data, isFetching, q } = useSearchResults(props.query, props.tools);
    return (
        <Panel title={<h2 className="flex items-center gap-2">Results {isFetching ? <Spinner className="size-4" /> : null}</h2>} flush>
            <SearchGroups data={data} q={q} />
        </Panel>
    );
}

// ---------------------------------------------------------------------------------------------
// Needs attention
// ---------------------------------------------------------------------------------------------

function Attention(props: Readonly<{ home: HomeResponse | undefined; loading: boolean; error: Error | null; tools: readonly ToolMeta[] }>) {
    // Red first (costing money, out of date), then amber; each tool's own order within that.
    const alerts = props.tools
        .flatMap((t) => (props.home?.tools[t.id]?.alerts ?? []).filter((a) => !a.sidebarOnly).map((a) => ({ tool: t, alert: a })))
        .sort((a, b) => Number(b.alert.tone === "red") - Number(a.alert.tone === "red"));
    let body = (
        <ul className="m-0 list-none p-0">
            {alerts.map(({ tool, alert }) => (
                <AlertRow key={`${tool.id}:${alert.id}`} tool={tool} alert={alert} showTool={props.tools.length > 1} />
            ))}
        </ul>
    );
    if (props.loading) body = <div className="grid place-items-center p-6"><Spinner /></div>;
    // Not loaded: said, not "All caught up".
    else if (props.error) body = <div className="p-4"><ErrorNotice error={props.error} /></div>;
    else if (!alerts.length) {
        body = (
            <p className="flex items-center gap-2 p-4 text-sm text-muted">
                <CircleCheck className="size-5 text-brand-500" /> All caught up: nothing needs looking at.
            </p>
        );
    }
    return (
        <Panel title="Needs attention" flush>
            {body}
        </Panel>
    );
}

function AlertRow({ tool, alert, showTool }: Readonly<{ tool: ToolMeta; alert: HomeAlert; showTool: boolean }>) {
    const withWindows = useWithWindows();
    return (
        <li className="border-b border-line last:border-b-0">
            <Link href={withWindows(alert.href)} className="flex items-center gap-3 px-4 py-3 text-ink no-underline hover:bg-surface-2">
                <TriangleAlert className={clsx("size-5 shrink-0", alert.tone === "red" ? "text-danger" : "text-warning")} />
                <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-baseline gap-x-2">
                        <span className="font-semibold">
                            <span className="tabular-nums">{alert.count.toLocaleString()}</span> {alert.label}
                        </span>
                        {showTool ? <Badge>{tool.name}</Badge> : null}
                    </span>
                    {alert.detail ? <span className="block text-xs text-muted">{alert.detail}</span> : null}
                </span>
                <ChevronRight className="size-4 shrink-0 text-muted" />
            </Link>
        </li>
    );
}

// ---------------------------------------------------------------------------------------------
// Setting up
// ---------------------------------------------------------------------------------------------

/** Setup still to do, as bars (each opens the ones left). Gone once everything's done. */
function Setup(props: Readonly<{ home: HomeResponse | undefined; tools: readonly ToolMeta[] }>) {
    const rows = props.tools.flatMap((t) => (props.home?.tools[t.id]?.setup ?? []).map((p) => ({ tool: t, progress: p })));
    if (!rows.length) return null;
    return (
        <Panel title="Setting up" flush>
            <ul className="m-0 list-none p-0">
                {rows.map(({ tool, progress }) => (
                    <SetupRow key={`${tool.id}:${progress.id}`} tool={tool} progress={progress} showTool={props.tools.length > 1} />
                ))}
            </ul>
        </Panel>
    );
}

function SetupRow({ tool, progress, showTool }: Readonly<{ tool: ToolMeta; progress: HomeProgress; showTool: boolean }>) {
    const withWindows = useWithWindows();
    const { done, total } = progress;
    return (
        <li className="border-b border-line last:border-b-0">
            <Link href={withWindows(progress.href)} className="flex items-center gap-3 px-4 py-3 text-ink no-underline hover:bg-surface-2" title={`See the ${(total - done).toLocaleString()} still to do`}>
                <span className="min-w-0 flex-1">
                    <span className="mb-1.5 flex flex-wrap items-baseline justify-between gap-x-3">
                        <span className="flex items-baseline gap-2 font-semibold">
                            {progress.label}
                            {showTool ? <Badge>{tool.name}</Badge> : null}
                        </span>
                        <span className="text-xs text-muted tabular-nums">
                            {done.toLocaleString()} of {total.toLocaleString()} {progress.unit}
                        </span>
                    </span>
                    <ProgressBar value={done / total} label={`${progress.label}: ${done} of ${total}`} />
                </span>
                <ChevronRight className="size-4 shrink-0 text-muted" />
            </Link>
        </li>
    );
}

// ---------------------------------------------------------------------------------------------
// Tools
// ---------------------------------------------------------------------------------------------

function ToolCards(props: Readonly<{ home: HomeResponse | undefined; tools: readonly ToolMeta[] }>) {
    const withWindows = useWithWindows();
    return (
        <Panel title="Tools" flush>
            <ul className="m-0 list-none p-0">
                {props.tools.flatMap((t) => {
                    const home = props.home?.tools[t.id];
                    // A tool's own rows (the Price List's Catalogue has one too), or the one with its name.
                    const cards: HomeCard[] = home?.cards ?? [{ id: t.id, name: t.name, icon: t.icon, href: `/t/${t.id}`, stats: home?.stats ?? [] }];
                    return cards.map((card, i) => (
                        <li key={`${t.id}:${card.id}`} className="border-b border-line last:border-b-0">
                            <Link href={withWindows(card.href)} className="flex items-start gap-3 px-4 py-3 no-underline hover:bg-surface-2">
                                <Icon name={card.icon} className="mt-0.5 size-5 shrink-0 text-brand-ink" />
                                <span className="min-w-0 flex-1">
                                    <span className="flex items-center gap-2 font-semibold text-ink">
                                        {card.name}
                                        {t.beta && i === 0 ? <Badge tone="blue">Beta</Badge> : null}
                                    </span>
                                    <span className="block text-xs text-muted">{card.stats.length ? card.stats.join(" · ") : t.description}</span>
                                </span>
                            </Link>
                        </li>
                    ));
                })}
                {/* Tools on the way: not links. */}
                {comingSoonTools.map((t) => (
                    <li key={t.id} className="flex items-start gap-3 border-b border-line px-4 py-3 last:border-b-0" aria-disabled="true">
                        <Icon name={t.icon} className="mt-0.5 size-5 shrink-0 text-muted" />
                        <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-2 font-semibold text-muted">
                                {t.name}
                                <Badge>Coming soon</Badge>
                            </span>
                            <span className="block text-xs text-muted">{t.description}</span>
                        </span>
                    </li>
                ))}
            </ul>
        </Panel>
    );
}
